import { getProctorAllocation } from "../../native-modules/tare/getProctorAllocation";
import type { ProctorSpecification } from "../../proctor/ProctorSpecification";

export const RnGhostwriterTest: ProctorSpecification = {
  name: "rn_ghostwriter_test",
  buckets: {
    inactive: -1,
    control: 0,
    async_auto_apply_submit: 1,
    sync_prefill_submit: 2,
    deprecated: 3,
    // 4 retired (was phase_c); kept as a reserved slot so the value isn't reused.
    reserved: 4,
    hybrid_automation: 5,
    sync_auto_apply: 6,
  },
};

const RN_GHOSTWRITER_ASYNC_AUTO_APPLY_BUCKET = RnGhostwriterTest.buckets.async_auto_apply_submit;
const RN_GHOSTWRITER_SYNC_PREFILL_BUCKET = RnGhostwriterTest.buckets.sync_prefill_submit;
const RN_GHOSTWRITER_DEPRECATED_BUCKET = RnGhostwriterTest.buckets.deprecated;
const RN_GHOSTWRITER_HYBRID_AUTOMATION_BUCKET = RnGhostwriterTest.buckets.hybrid_automation;
const RN_GHOSTWRITER_SYNC_AUTO_APPLY_BUCKET = RnGhostwriterTest.buckets.sync_auto_apply;

/**
 * Resolved allocation for any bucket whose proctor payload carries a `jobKeys`
 * list (`sync_prefill_submit`, `deprecated`, `sync_auto_apply`). Per-bucket
 * semantics live on the `RnGhostwriterAllocation` fields below.
 *
 * - `inBucket`: whether the user is actually allocated to (or forced into) the
 *   bucket.
 * - `jobKeys`: the list parsed from the proctor payload
 *   (`{ "jobKeys": "stringArray" }`). Empty when not in the bucket, when the
 *   payload is missing (e.g. bucket forced via debug menu, which doesn't carry
 *   a payload), or when the payload is present but malformed. Callers need both
 *   fields to distinguish "in bucket with a usable payload" from "in bucket but
 *   no payload" (fall back to the normal eligibility flow).
 */
export interface RnGhostwriterPayloadAllocation {
  inBucket: boolean;
  jobKeys: string[];
}

/**
 * Single source of truth for `rn_ghostwriter_test` state.
 *
 * Resolved from one bridge call so the derived flags are always mutually
 * consistent — splitting this across multiple `getProctorAllocation` calls
 * would allow a transient bridge failure (or a mid-flight matrix refresh) to
 * persist an impossible combination like `isAsyncAutoApply && deprecated.inBucket`.
 */
export interface RnGhostwriterAllocation {
  isAsyncAutoApply: boolean;
  /**
   * `sync_prefill_submit`: `jobKeys` is a traffic-cap allowlist (NOT pre-seeded
   * eligibility) — ViewJob runs a real-time per-job eligibility read for
   * allowlisted jobs, like `sync_auto_apply`.
   */
  syncPrefill: RnGhostwriterPayloadAllocation;
  /**
   * `hybrid_automation` reuses `sync_prefill_submit`'s Prefill synchronous UI
   * flow and CTA copy verbatim, and additionally surfaces the CAPTCHA / 2FA
   * intervention screen (the daemon's Browserless live URL) when a submission
   * parks in `AWAITING_USER_INTERVENTION`. See RN-3367.
   */
  isHybridAutomation: boolean;
  /**
   * `deprecated`: `jobKeys` pre-seeds eligibility (fast-path skips the backend
   * eligibility check).
   */
  deprecated: RnGhostwriterPayloadAllocation;
  /**
   * `sync_auto_apply`: `jobKeys` is a traffic-cap allowlist — eligibility comes
   * from a real-time per-job ViewJob read, not the payload.
   */
  syncAutoApply: RnGhostwriterPayloadAllocation;
}

const EMPTY_ALLOCATION: RnGhostwriterAllocation = {
  isAsyncAutoApply: false,
  syncPrefill: { inBucket: false, jobKeys: [] },
  isHybridAutomation: false,
  deprecated: { inBucket: false, jobKeys: [] },
  syncAutoApply: { inBucket: false, jobKeys: [] },
};

export async function resolveRnGhostwriterAllocation(): Promise<RnGhostwriterAllocation> {
  const allocation = await getProctorAllocation(RnGhostwriterTest);
  if (allocation == null) return EMPTY_ALLOCATION;
  return {
    isAsyncAutoApply: allocation.value === RN_GHOSTWRITER_ASYNC_AUTO_APPLY_BUCKET,
    syncPrefill:
      allocation.value === RN_GHOSTWRITER_SYNC_PREFILL_BUCKET
        ? { inBucket: true, jobKeys: parseJobKeysPayload(allocation.payload) }
        : { inBucket: false, jobKeys: [] },
    isHybridAutomation: allocation.value === RN_GHOSTWRITER_HYBRID_AUTOMATION_BUCKET,
    deprecated:
      allocation.value === RN_GHOSTWRITER_DEPRECATED_BUCKET
        ? { inBucket: true, jobKeys: parseJobKeysPayload(allocation.payload) }
        : { inBucket: false, jobKeys: [] },
    syncAutoApply:
      allocation.value === RN_GHOSTWRITER_SYNC_AUTO_APPLY_BUCKET
        ? { inBucket: true, jobKeys: parseJobKeysPayload(allocation.payload) }
        : { inBucket: false, jobKeys: [] },
  };
}

// The TARE bridge may deliver proctor payloads as multi-level JSON-encoded
// strings (Android in particular has historically double-encoded payloads —
// see `parseHpSectionProctorPayload`). Re-parse while still a string, capped
// to avoid pathological inputs.
const MAX_PAYLOAD_PARSE_DEPTH = 5;

/**
 * Tolerant of three observed payload shapes:
 *
 *  - Object: `{ jobKeys: ["…"] }` — what the spec (`type: "map"`) implies and
 *    what the production bridge now returns.
 *  - Bare array: `["…"]` — defensive; in case a future SDK / serializer
 *    change flattens the single-field map to its inner stringArray.
 *  - Either of the above, wrapped in 1–N layers of JSON string encoding.
 *
 * Shared by every bucket whose payload carries a jobKeys list
 * (sync_prefill_submit, deprecated, sync_auto_apply).
 */
function parseJobKeysPayload(payload: unknown): string[] {
  if (payload == null || payload === "") return [];
  try {
    let parsed: unknown = payload;
    for (let i = 0; i < MAX_PAYLOAD_PARSE_DEPTH && typeof parsed === "string"; i++) {
      parsed = JSON.parse(parsed);
    }
    if (Array.isArray(parsed)) {
      return parsed.filter((k): k is string => typeof k === "string");
    }
    if (parsed != null && typeof parsed === "object") {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const jobKeys = (parsed as { jobKeys?: unknown }).jobKeys;
      if (Array.isArray(jobKeys)) {
        return jobKeys.filter((k): k is string => typeof k === "string");
      }
    }
    return [];
  } catch {
    return [];
  }
}
